import React, { useState, useEffect } from 'react';
import { fetchUsers, awardPackToUser, awardMoneyToUser } from '../../api/user';
import { awardItemToUser, ITEM_TYPES } from '../../api/items';
import { toast } from 'react-toastify';
import useSeries from '../../hooks/useSeries';
import LoadingSpinner from '../../components/LoadingSpinner';

const formatDate = dateStr => {
    if (!dateStr) return '—';
    return new Date(dateStr).toLocaleString('en-US', {
        month: 'short', day: 'numeric', year: 'numeric',
        hour: 'numeric', minute: '2-digit', hour12: true,
    });
};

const AdminUserManagementPage = () => {
    const [users, setUsers] = useState([]);
    const packs = useSeries();
    const [loading, setLoading] = useState(true);
    const [sortField, setSortField] = useState('lastLogin');
    const [sortDir, setSortDir] = useState('desc');

    const [awardMoney, setAwardMoney] = useState({});
    const [awardPack, setAwardPack] = useState({});
    const [awardItems, setAwardItems] = useState({});

    // Fetch users when component mounts
    useEffect(() => {
        const getUsers = async () => {
            setLoading(true);
            try {
                const data = await fetchUsers(); // Fetches users from the API
                setUsers(data.users);
            } catch (error) {
                toast.error('Error fetching users');
            } finally {
                setLoading(false);
            }
        };

        getUsers();
    }, []);


    // Handles awarding a static pack to the user
    const handleAwardPack = async userID => {
        if (awardPack[userID]) {
            try {
                await awardPackToUser({ userID, seriesID: awardPack[userID] }); // API call to award the pack
                toast.success(`Pack awarded to user with ID: ${userID}`);
            } catch (error) {
                toast.error('Error awarding pack:', error);
            }
        }
    };

    // Handles awarding money to the user
    const handleAwardMoney = async userID => {
        //{ userID, amount, reason }
        if (awardMoney[userID]) {
            try {
                await awardMoneyToUser({ userID, amount: awardMoney[userID] }); // API call to award money
                toast.success(`Money awarded to user with ID: ${userID}`);
            } catch (error) {
                toast.error('Error awarding money:', error);
            }
        }
    };

    const handleAwardItem = async userID => {
        const amount = Number(awardItems[userID]);
        if (!amount || amount < 1) return;
        try {
            await awardItemToUser({ userID, itemType: ITEM_TYPES.ENERGY_DRINK, amount });
            toast.success(`Awarded ${amount} energy drink(s) to user`);
            setAwardItems(prev => ({ ...prev, [userID]: '' }));
        } catch (error) {
            toast.error('Error awarding item: ' + (error.response?.data?.error || error.message));
        }
    };

    const handleSort = field => {
        if (sortField === field) {
            setSortDir(d => (d === 'asc' ? 'desc' : 'asc'));
        } else {
            setSortField(field);
            setSortDir('asc');
        }
    };

    const sortedUsers = [...users].sort((a, b) => {
        let aVal = a[sortField] ?? '';
        let bVal = b[sortField] ?? '';
        if (sortField === 'lastLogin' || sortField === 'created') {
            aVal = aVal ? new Date(aVal).getTime() : 0;
            bVal = bVal ? new Date(bVal).getTime() : 0;
        } else if (sortField === 'exp') {
            aVal = Number(aVal) || 0;
            bVal = Number(bVal) || 0;
        } else {
            aVal = aVal.toString().toLowerCase();
            bVal = bVal.toString().toLowerCase();
        }
        if (aVal < bVal) return sortDir === 'asc' ? -1 : 1;
        if (aVal > bVal) return sortDir === 'asc' ? 1 : -1;
        return 0;
    });

    const sortIcon = field => {
        if (sortField !== field) return ' ↕';
        return sortDir === 'asc' ? ' ↑' : ' ↓';
    };

    if (loading) {
        return <LoadingSpinner />;
    }

    return (
        <div className="admin-page">
            <h2>User Management</h2>

            <div className="table-scroll-wrapper">
                <table>
                    <thead>
                        <tr>
                            <th className="sortable" onClick={() => handleSort('username')}>
                                Username{sortIcon('username')}
                            </th>
                            <th className="sortable" onClick={() => handleSort('created')}>
                                Created{sortIcon('created')}
                            </th>
                            <th className="sortable" onClick={() => handleSort('lastLogin')}>
                                Last Login{sortIcon('lastLogin')}
                            </th>
                            <th className="sortable" onClick={() => handleSort('exp')}>
                                EXP{sortIcon('exp')}
                            </th>
                            <th>Award Pack</th>
                            <th>Award Money</th>
                            <th>Award Energy Drinks</th>
                        </tr>
                    </thead>
                    <tbody>
                        {sortedUsers.map(user => (
                            <tr key={user.userID}>
                                <td><a href={`/u/${user.username}`} target="_blank" rel="noopener noreferrer">{user.username}</a><br /><small>{user.email?.split('@')[0]}</small></td>
                                <td>{formatDate(user.created)}</td>
                                <td>{formatDate(user.lastLogin)}</td>
                                <td>{user.exp ?? 0}</td>

                                <td>
                                    <div className="cell-action">
                                        <select
                                            value={awardPack[user.userID] || ''}
                                            onChange={e => setAwardPack({ ...awardPack, [user.userID]: e.target.value })}
                                        >
                                            <option value="">Select Pack</option>
                                            {packs.map(pack => (
                                                <option key={pack.seriesID} value={pack.seriesID}>
                                                    {pack.name}
                                                </option>
                                            ))}
                                        </select>
                                        <button onClick={() => handleAwardPack(user.userID)}>A</button>
                                    </div>
                                </td>

                                <td>
                                    <div className="cell-action">
                                        <input
                                            type="number"
                                            value={awardMoney[user.userID] || ''}
                                            onChange={e => setAwardMoney({ ...awardMoney, [user.userID]: e.target.value })}
                                            placeholder="Amount"
                                        />
                                        <button onClick={() => handleAwardMoney(user.userID)}>A</button>
                                    </div>
                                </td>

                                <td>
                                    <div className="cell-action">
                                        <input
                                            type="number"
                                            min="1"
                                            value={awardItems[user.userID] || ''}
                                            onChange={e => setAwardItems(prev => ({ ...prev, [user.userID]: e.target.value }))}
                                            placeholder="Amount"
                                        />
                                        <button onClick={() => handleAwardItem(user.userID)}>A</button>
                                    </div>
                                </td>
                            </tr>
                        ))}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default AdminUserManagementPage;
